import {
   useGetPhoneNumbers,
   useAssignPhoneNumberToCampaign,
   useRemovePhoneNumbers,
   useUpdateRealtorNumber,
} from '../queries/phone-number-management.queries'
import { useMemo } from 'react'
import {
   getGroupedPhoneNumbers,
   getNumbersGroupedByCampaignId,
   getUniqueCampaignIds,
   getCampaignListWithCounts,
} from '../utils'
import { IPhoneNumber } from '../interface'
import { useActiveCampaigns } from '@forwardflow/features/campaign'

export const usePhoneNumbers = () => {
   const { data: phoneNumbers = [], isLoading } = useGetPhoneNumbers() as unknown as {
      data: IPhoneNumber[]
      isLoading: boolean
   }
   const { data: campaigns = [] } = useActiveCampaigns({
      page: 1,
      limit: 100,
      status: 'active,paused',
   })
   const { assignPhoneNumber } = useAssignPhoneNumberToCampaign()
   const { removePhoneNumbers } = useRemovePhoneNumbers()
   const { updateRealtorNumber } = useUpdateRealtorNumber()

   const uniqueCampaignIds = useMemo(() => {
      return getUniqueCampaignIds(phoneNumbers as IPhoneNumber[])
   }, [phoneNumbers])

   const campaignList = useMemo(() => {
      return getCampaignListWithCounts(
         campaigns,
         phoneNumbers as IPhoneNumber[],
         true
      )
   }, [campaigns, phoneNumbers])

   const allCampaigns = useMemo(() => {
      return getCampaignListWithCounts(
         campaigns,
         phoneNumbers as IPhoneNumber[],
         false
      )
   }, [campaigns, phoneNumbers])

   const useCampaignPhoneNumberGroups = (campaignId: string) => {
      return useMemo(() => {
         return getNumbersGroupedByCampaignId(
            phoneNumbers as IPhoneNumber[],
            campaignId
         )
      }, [campaignId])
   }

   const {
      activePhoneNumbers,
      inactivePhoneNumbers,
      realtorNumber,
      groupedActivePhoneNumbers,
      groupedInactivePhoneNumbers,
   } = useMemo(() => {
      const activePhoneNumbers =
         phoneNumbers?.filter(
            (p) => p?.campaignCount > 0 && !p?.phoneNumber?.ffNumber
         ) ?? []

      const inactivePhoneNumbers =
         phoneNumbers?.filter(
            (p) =>
               (p?.campaignCount === 0 || !p?.campaignCount) &&
               !p?.phoneNumber?.ffNumber
         ) ?? []
      const realtorNumber = phoneNumbers?.find((p) => p?.phoneNumber?.ffNumber)

      // Group the phone numbers by area code
      const groupedActivePhoneNumbers = getGroupedPhoneNumbers(
         activePhoneNumbers as IPhoneNumber[]
      )
      const groupedInactivePhoneNumbers = getGroupedPhoneNumbers(
         inactivePhoneNumbers as IPhoneNumber[]
      )

      return {
         activePhoneNumbers,
         inactivePhoneNumbers,
         realtorNumber,
         groupedActivePhoneNumbers,
         groupedInactivePhoneNumbers,
      }
   }, [phoneNumbers])

   // Handler functions
   const handleAssignPhoneNumber = async (
      phoneNumbers: string[],
      campaignId: string
   ) => {
      try {
         await assignPhoneNumber(phoneNumbers, campaignId)
      } catch (error) {
         console.error('Error assigning phone numbers:', error)
         throw error
      }
   }

   const handleRemovePhoneNumbers = async (phoneNumbers: string[]) => {
      try {
         await removePhoneNumbers(phoneNumbers)
      } catch (error) {
         console.error('Error removing phone numbers:', error)
         throw error
      }
   }

   const handleUpdateRealtorNumber = async (phoneNumber: string) => {
      try {
         await updateRealtorNumber(phoneNumber)
      } catch (error) {
         console.error('Error updating realtor number:', error)
         throw error
      }
   }

   return {
      activePhoneNumbers,
      inactivePhoneNumbers,
      realtorNumber,
      groupedActivePhoneNumbers,
      groupedInactivePhoneNumbers,
      isLoadingPhoneNumbers: isLoading,
      uniqueCampaignIds,
      campaignList,
      allCampaigns,
      useCampaignPhoneNumberGroups,
      // Handlers
      assignPhoneNumber: handleAssignPhoneNumber,
      removePhoneNumbers: handleRemovePhoneNumbers,
      updateRealtorNumber: handleUpdateRealtorNumber,
   }
}
